import { supabase } from "../../lib/supabaseClient";

// --- Make an uploaded filename safe to embed in a URL / path ---
// The DWG upload server stores the file under its original name and returns a URL
// pointing at it, which we then hand to Unreal to download. Any character that
// isn't URL-safe corrupts that URL and makes the download fail. The usual culprit
// is a space ("floor plan.dwg" → "floor plan.dwg" in the URL), but plenty of other
// characters break a URL just as badly:
//   space / tab        → become %20 or split the URL
//   # ? & = +          → treated as fragment / query-string delimiters
//   %                  → starts a percent-escape and mangles decoding
//   / \                → treated as extra path separators
//   : # @              → authority / scheme delimiters
//   ' " < > | ^ { } [ ] ` → unsafe/illegal in URLs
//   é ñ 你 etc.        → non-ASCII, need encoding to survive a raw URL
// So we normalize the name down to [A-Za-z0-9._-] before uploading.
export function sanitizeFileName(name) {
  const raw = String(name || "").trim();

  // Split off the extension so we never touch the dot that precedes it.
  const dot = raw.lastIndexOf(".");
  let base = dot > 0 ? raw.slice(0, dot) : raw;
  let ext = dot > 0 ? raw.slice(dot + 1) : "";

  const clean = (s) =>
    s
      .normalize("NFKD") // split accented letters into base char + diacritic
      .replace(/\p{Diacritic}/gu, "") // drop the diacritic marks (é → e)
      .replace(/[^A-Za-z0-9._-]+/g, "_") // spaces, #, ?, &, %, / … → _
      .replace(/_+/g, "_") // collapse runs of underscores
      .replace(/^[._-]+|[._-]+$/g, ""); // trim leading/trailing separators

  base = clean(base) || "file";
  ext = clean(ext);

  return ext ? `${base}.${ext}` : base;
}

// Stopgap while EC2 still runs the pre-release file server (the website
// shipped first): that server replies with `path`, not `url`, and refuses a
// sourceUrl because it only takes a file. In that case the image Unreal
// already uploaded is recorded where it is, not copied under the project.
// Remove once the new file server is live.
export function storedScreenshotUrl(ok, body, sourceUrl) {
  if (ok) return body.url || body.path || "";
  return sourceUrl || "";
}

// A walkthrough screenshot: stored on the file server under the project and
// build key, then recorded in the screenshots table the project page lists.
// `sourceUrl` is an image Unreal already uploaded (the server copies it under
// the project); `blob` is one we captured ourselves.
export async function saveScreenshot({ build, sessionId, blob, sourceUrl }) {
  const form = new FormData();
  form.append("projectId", build.project_id);
  form.append("buildKey", build.key);
  form.append("sessionId", sessionId);
  if (blob) form.append("screenshot", blob, `screenshot-${Date.now()}.png`);
  else form.append("sourceUrl", sourceUrl);
  const res = await fetch("/api/upload-screenshot", { method: "POST", body: form });
  const body = await res.json().catch(() => ({}));
  const url = storedScreenshotUrl(res.ok, body, sourceUrl);
  if (!url) throw new Error(body.error || `upload failed: ${res.status}`);
  const { error } = await supabase.from("screenshots").insert({
    project_id: build.project_id, build_id: build.id, session_id: sessionId, url,
  });
  if (error) throw error;
  return body.url;
}

export async function listScreenshots(projectId) {
  const { data, error } = await supabase.from("screenshots")
    .select("id,url,session_id,created_at").eq("project_id", projectId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return groupBySession(data || []);
}

// Rows arrive newest first, so groups keep that order and each group's `ts` is
// its newest shot.
export function groupBySession(rows) {
  const groups = new Map();
  for (const r of rows) {
    if (!groups.has(r.session_id)) groups.set(r.session_id, { group: r.session_id, ts: r.created_at, items: [] });
    groups.get(r.session_id).items.push({ id: r.id, url: r.url, created: r.created_at });
  }
  return [...groups.values()];
}
